import { yupResolver } from '@hookform/resolvers/yup';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useEffect, useState, useTransition } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { useWarehouses } from '@/hooks/useWarehouses';
import { ProductResponse } from '@/providers/HttpProvider/Fulfill/Inventory/types';
import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { RootState } from '@/redux/store';
import { IReqCreateOrder } from '@/types/fulfill/inventory';
import { translateErrorMessage } from '@/utils/errorTranslations';

import type { AddressDetails } from '@/components/address-autocomplete';

import {
  COUNTRY_DIAL_CODES,
  COUNTRY_TO_PHONE_CODE,
  DEFAULT_COUNTRY,
  MAX_PHONE_LENGTH_BY_COUNTRY,
  sendToClientSchema,
} from '../inventorySchema';

const mapProfileCountryToPhoneCode = (country?: string): 'MX' | 'CO' | 'CL' | 'PE' => {
  const mapping: Record<string, 'MX' | 'CO' | 'CL' | 'PE'> = {
    MEX: 'MX',
    COL: 'CO',
    CHL: 'CL',
    PER: 'PE',
  };
  return mapping[country || ''] || 'MX';
};

const renderErrorToastContent = (title: string, description?: string) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div style={{ fontWeight: 600, fontSize: '14px' }}>{title}</div>
      {description && (
        <div
          style={{ fontSize: '12px', color: '#4a4a4a', fontWeight: 'normal', lineHeight: '1.4' }}
        >
          {description}
        </div>
      )}
    </div>
  );
};

interface FormItemData {
  sku: string;
  quantity: number;
}

type SendToClientFormData = Yup.InferType<typeof sendToClientSchema>;

interface EditItems {
  index: number;
  sku: string;
  quantity: string;
}

interface useSendToClientProps {
  products: ProductResponse['products'];
  onClose: () => void;
}

const useSendToClient = ({ products, onClose }: useSendToClientProps) => {
  const theme = useTheme();
  const [currentStep, setCurrentStep] = useState(1);
  const [editStep, setEditStep] = useState(false);
  const { profile } = useSelector((state: RootState) => state.parcel);

  const hasTLM = profile.data?.configuration?.services?.tailor99 ?? false;
  const initialCountry = mapProfileCountryToPhoneCode(profile.data?.country);
  const [selectedCountryPhone, setSelectedCountryPhone] = useState<'MX' | 'CO' | 'CL' | 'PE'>(
    initialCountry,
  );
  const [isPending] = useTransition();
  const [warehouse, setWarehouse] = useState('');
  const [isAddressConfirmed, setIsAddressConfirmed] = useState(false);
  const [isAddressTouched, setIsAddressTouched] = useState(false);
  const { warehouses, warehouseOptions, isLoading: isLoadingWarehouses } = useWarehouses();
  const http = initializeHttpProvider();

  const {
    register,
    control,
    formState: { errors },
    setValue,
    trigger,
    getValues,
    handleSubmit,
    watch,
  } = useForm<SendToClientFormData>({
    resolver: yupResolver(sendToClientSchema),
    context: { country: selectedCountryPhone, hasTLM },
    mode: 'onBlur',
    defaultValues: {
      deliveryType: 'SPT',
      recipient: {
        phone: COUNTRY_DIAL_CODES[initialCountry] || COUNTRY_DIAL_CODES[DEFAULT_COUNTRY],
      },
    },
  });

  useEffect(() => {
    if (!profile.data?.country) return;

    const phoneCountry = mapProfileCountryToPhoneCode(profile.data.country);
    if (phoneCountry === selectedCountryPhone) return;

    setSelectedCountryPhone(phoneCountry);

    const currentPhone = getValues('recipient.phone') || '';
    const isStillInitialDialCode = Object.values(COUNTRY_DIAL_CODES).includes(currentPhone);

    if (isStillInitialDialCode || !currentPhone) {
      setValue('recipient.phone', COUNTRY_DIAL_CODES[phoneCountry]);
    }
  }, [profile.data?.country]);

  useEffect(() => {
    if (profile.data?.country) return;

    if (isLoadingWarehouses || warehouses.length === 0) return;

    const warehouseCountry = warehouses[0].location.country;
    const phoneCountry = COUNTRY_TO_PHONE_CODE[warehouseCountry] || DEFAULT_COUNTRY;

    if (phoneCountry === selectedCountryPhone) return;

    setSelectedCountryPhone(phoneCountry);

    const currentPhone = getValues('recipient.phone') || '';
    const isStillInitialDialCode = Object.values(COUNTRY_DIAL_CODES).includes(currentPhone);

    if (isStillInitialDialCode) {
      setValue('recipient.phone', COUNTRY_DIAL_CODES[phoneCountry]);
    }
  }, [warehouses, isLoadingWarehouses, profile.data?.country]);

  const handleChangeStep = (step: number) => {
    setCurrentStep(step);
  };

  const handleChangeSelectedCountryPhone = (country: string) => {
    setSelectedCountryPhone(country as 'MX' | 'CO' | 'CL' | 'PE');
  };

  const onChangeItems = ({ index, sku, quantity }: EditItems) => {
    const quantityParsed = parseInt(quantity.trim(), 10);
    setValue(`items.${index}.sku`, sku);
    setValue(
      `items.${index}.quantity`,
      (Number.isFinite(quantityParsed) ? quantityParsed : 0) as number,
    );
    trigger('items');
  };

  const onWarehouseChange = (warehouseValue: string) => {
    setWarehouse(warehouseValue);
    setValue('warehouse', warehouseValue);
    trigger('warehouse');
  };

  const onAddressChange = (suggest: AddressDetails) => {
    if (suggest) {
      setValue('destination.address', suggest.address || suggest.label);

      const country =
        findAddressComponent(suggest.gmaps?.address_components, 'country', 'short_name') ||
        suggest.country;

      // Extract the postal code from Google Maps components
      const postalCode =
        suggest.zipCode ||
        findAddressComponent(suggest.gmaps?.address_components, 'postal_code', 'long_name');

      // Convert Google country code (ISO 3166-1 alpha-2) to the format used in the system
      const countryCode = mapGoogleCountryToSystemCountry(country);
      setValue('destination.country', countryCode);
      setValue('destination.zipcode', postalCode ? String(postalCode) : '');

      setValue('destination.lat', suggest.latitude);
      setValue('destination.lng', suggest.longitude);

      // Mark the address as confirmed only if it has a country
      setIsAddressConfirmed(!!countryCode);

      trigger('destination');
    } else {
      // If there is no valid suggestion, reset the confirmation
      setIsAddressConfirmed(false);
    }
  };

  const onAddressManualChange = () => {
    // Reset confirmation, country and zipcode when the user manually edits the address
    setIsAddressConfirmed(false);
    setValue('destination.country', '');
    setValue('destination.lat', 0);
    setValue('destination.lng', 0);
    setValue('destination.zipcode', '');
  };

  const onAddressBlur = () => {
    setIsAddressTouched(true);
  };

  const isDisabled = Boolean(
    'items' in errors ||
    getValues('items') === undefined ||
    getValues('items')?.some((item) => !item?.sku || !item?.quantity) ||
    getValues('items')?.length !== products.length,
  );

  const isStepDisabled = (currentStep: number) => {
    switch (currentStep) {
      case 1:
        return Boolean(
          errors.items ||
          !getValues('items') ||
          getValues('items')?.some((item) => !item?.sku || !item?.quantity) ||
          getValues('items')?.length !== products.length,
        );

      case 2: {
        const destinationCountry = watch('destination.country');
        const zipcodeRequired = destinationCountry === 'MEX' || destinationCountry === 'COL';
        const phone = (watch('recipient')?.phone || '').replace(/\s/g, '');
        const requiredPhoneLength = MAX_PHONE_LENGTH_BY_COUNTRY[selectedCountryPhone] || 13;
        return Boolean(
          errors.recipient ||
          !watch('recipient') ||
          !watch('recipient')?.firstName ||
          !watch('recipient')?.lastName ||
          phone.length < requiredPhoneLength ||
          !watch('recipient')?.email ||
          errors.destination ||
          !watch('destination')?.address ||
          (zipcodeRequired && !watch('destination')?.zipcode) ||
          !isAddressConfirmed ||
          !watch('destination')?.country,
        );
      }

      case 3:
        return Boolean(
          errors.payments ||
          errors.options ||
          errors.internalKey ||
          errors.price ||
          errors.deliveryType,
        );
      default:
        return false;
    }
  };

  const createOrder = async (data: SendToClientFormData) => {
    const request = formatRequest(data, products, warehouse);
    const completeRequest = {
      draft: false,
      shipments: [
        {
          ...request,
          sender: {
            ...profile.data?.profile,
          },
        },
      ],
    };

    try {
      await http.inventory.createOrder(completeRequest as IReqCreateOrder);

      toast.success(`La orden se ha creado exitosamente`);
      onClose();
    } catch (e: unknown) {
      let msg = '';

      const httpError = e as { response?: { json?: () => Promise<{ message?: string }> } };
      if (
        httpError &&
        typeof httpError === 'object' &&
        typeof httpError.response?.json === 'function'
      ) {
        try {
          const errorData = await httpError.response.json();
          msg = errorData?.message || '';
        } catch {
          // Fallback si falla el parseo del JSON
        }
      }

      if (!msg) {
        if (e instanceof Error) {
          msg = e.message;
        } else if (typeof e === 'string') {
          msg = e;
        } else if (e && typeof e === 'object' && 'message' in e) {
          msg = String((e as Record<string, unknown>).message);
        }
      }

      let errorDescription = translateErrorMessage(msg);

      if (
        !errorDescription ||
        errorDescription.trim() === '' ||
        errorDescription === 'Error desconocido'
      ) {
        errorDescription =
          'Por favor, verifica la información del destinatario, las dimensiones del paquete y la cobertura antes de intentar nuevamente.';
      }

      toast.error(renderErrorToastContent('Error al crear la orden', errorDescription), {
        autoClose: 6000,
      });
    }
  };

  const goEdit = (step: number) => {
    setEditStep(true);
    setCurrentStep(step);
  };

  const totalUnits =
    getValues('items')?.reduce((sum, product) => {
      return sum + (product?.quantity || 0);
    }, 0) || 0;

  const estimatedVolume =
    getValues('items')?.reduce((totalVolume, item) => {
      const product = products.find((product) => product.sku === item?.sku);
      if (product?.dimensions) {
        const { length, width, height } = product.dimensions;
        const volumeInCm3 = length * width * height * (item?.quantity || 0);
        return totalVolume + volumeInCm3;
      }
      return totalVolume;
    }, 0) || 0;

  const getDeliveryTypeName = (type?: string) => {
    switch (type) {
      case 'SPT':
        return 'Sprint';
      case 'NXD':
        return 'Next Day';
      case 'NAT':
        return 'Nacional';
      case 'CO2':
        return 'CO2 Free';
      case 'TLM':
        return 'TailorMade';
      default:
        return type || 'Sprint';
    }
  };

  const drawerData = [
    {
      step: 1,
      title: `Estás por enviar ${totalUnits} unidades`,
      description: (
        <Box>
          <Typography variant="body1">
            <strong>Volumen estimado:</strong> {estimatedVolume.toFixed(0)} cm³
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.primary.main }}>
            Total de unidades: <strong>{totalUnits}</strong>
          </Typography>
          <Typography variant="body2">
            <strong>Almacén:</strong>{' '}
            {watch('warehouse')
              ? warehouseOptions.find((w) => w.value === watch('warehouse'))?.label ||
                'Seleccionado'
              : 'Asignación automática'}
          </Typography>
        </Box>
      ),
      goEdit: goEdit,
    },
    {
      step: 2,
      title: 'Estás por hacer el envío a:',
      description: (
        <Box>
          <Typography variant="body1">
            {watch('recipient')?.firstName || ''} {watch('recipient')?.lastName || ''}
          </Typography>
          <Typography variant="body2">{watch('recipient')?.email || ''}</Typography>
          <Typography variant="body2">{watch('recipient')?.phone || ''}</Typography>
          <Typography variant="body2">{watch('destination')?.address || ''}</Typography>
        </Box>
      ),
      goEdit: goEdit,
    },
    {
      step: 3,
      title: 'Tipo de envío',
      description: (
        <Box>
          <Typography variant="body1">
            <strong>Tipo:</strong> {getDeliveryTypeName(watch('deliveryType'))}
          </Typography>
          <Typography variant="body2">
            <strong>Identificador:</strong> {watch('internalKey') || 'Sin nombre'}
          </Typography>
          <Typography variant="body2">
            <strong>Notas:</strong> {watch('options')?.notes || 'Sin notas'}
          </Typography>
          <Typography variant="body2">
            <strong>COD:</strong>{' '}
            {(watch('payments')?.cashOnDelivery?.amount || 0) > 0
              ? `$${watch('payments')?.cashOnDelivery?.amount}`
              : 'No aplica'}
          </Typography>
          <Typography variant="body2">
            <strong>Seguro:</strong>{' '}
            {(watch('price')?.shippingPrice || 0) > 0
              ? `$${watch('price')?.shippingPrice}`
              : 'No aplica'}
          </Typography>
        </Box>
      ),
      goEdit: goEdit,
    },
  ];

  return {
    register,
    control,
    errors,
    watch,
    handleChangeStep,
    currentStep,
    onChangeItems,
    onWarehouseChange,
    onAddressChange,
    onAddressManualChange,
    onAddressBlur,
    warehouse: watch('warehouse') || '',
    warehouseOptions,
    isLoadingWarehouses,
    isDisabled,
    handleChangeSelectedCountryPhone,
    handleSubmit,
    createOrder,
    getValues,
    setCurrentStep,
    setEditStep,
    editStep,
    drawerData,
    isStepDisabled,
    isPending,
    isAddressConfirmed,
    isAddressTouched,
    selectedCountryPhone,
    hasTLM,
  };
};

export default useSendToClient;

// Helper function to extract address components from Google Maps
const findAddressComponent = (
  components: google.maps.GeocoderAddressComponent[] | undefined,
  type: string,
  version: string,
) => {
  const address = components?.find(function (component) {
    return component.types.indexOf(type) !== -1;
  });

  if (address != undefined) {
    return address[version as keyof google.maps.GeocoderAddressComponent];
  } else {
    return '';
  }
};

// Helper function to map Google country codes (ISO 3166-1 alpha-2) to the system format
const mapGoogleCountryToSystemCountry = (googleCountryCode: string | string[]): string => {
  const countryCode =
    typeof googleCountryCode === 'string' ? googleCountryCode : googleCountryCode[0] || '';

  const countryMapping: { [key: string]: string } = {
    MX: 'MEX',
    CL: 'CHL',
    CO: 'COL',
    PE: 'PER',
  };

  return countryMapping[countryCode] || 'MEX'; // Default to Mexico if the country code is not found in the mapping
};

const formatRequest = (
  data: SendToClientFormData,
  products: ProductResponse['products'],
  warehouse?: string,
) => {
  const { items, payments, destination, recipient, price, options, internalKey } = data;

  const formattedItems = items.flatMap((item: FormItemData) => {
    const product = products.find((product) => product.sku === item.sku);
    return Array(item.quantity).fill({
      description: product?.name,
      price: product?.price ?? 0,
      size: productSize(product?.dimensions?.weight ?? 0),
      sku: product?.sku,
      weight: product?.dimensions.weight,
    });
  });

  const formattedPayments = {
    ...payments,
    paymentMethod: 'cash',
    ...(payments?.cashOnDelivery && {
      cashOnDelivery: { ...payments.cashOnDelivery, currency: 'MXN' },
    }),
  };

  const { zipcode, country, ...restDestination } = destination;
  const formattedDestination = {
    ...restDestination,
    country: country || 'MEX', // Use the captured country or MEX by default
    ...(zipcode && { zipcode }), // Only include zipcode if it has a value (not required for CHL and PER)
  };

  const trimmedNotes = options?.notes?.trim();
  const formattedOptions = {
    ...options,
    notes: trimmedNotes && trimmedNotes.length > 0 ? trimmedNotes : 'Sin notas',
  };

  return {
    items: formattedItems,
    payments: formattedPayments,
    destination: formattedDestination,
    recipient,
    price,
    options: formattedOptions,
    deliveryType: data.deliveryType,
    internalKey,
    ...(warehouse && { warehouseId: warehouse }),
  };
};

const productSize = (weight: number) => {
  if (weight <= 1000) return 'xs';
  if (weight <= 3000) return 's';
  if (weight <= 5000) return 'm';
  if (weight <= 15000) return 'l';
  if (weight <= 25000) return 'xl';
};
